/**
 * Embed Sign
 *
 * One document's E-IMZO signing for a module that frames docs (zehn/hr): the
 * document beside docs' own SigningPanel. Signing happens here, on docs'
 * host, on purpose: the E-IMZO desktop client checks the API key against the
 * host of the page that connects to it, so a module signing in its own page
 * would need a paid key for its own domain. One key — docs' — covers every
 * module. The framer must pass Local Network Access down to this frame
 * (`allow="local-network-access <docs origin>"`), as the office does for the
 * modules it frames.
 *
 * Deep link: `/embed/sign?fileId=<id>`. A signer listed by PINFL alone can't
 * see the document until docs knows one of their certificates (a pending
 * slot is matched through a registered certificate), so a 404 first asks
 * them to confirm their E-IMZO key, then reloads.
 *
 * Tells the framer `{type: 'DOCS_SIGNING_CHANGED', fileId, change}` — change
 * `signed` after a signature, `changed` after a decline or withdrawal — so it
 * can re-read its own records.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KeyRound, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEmbedAuth } from '@/shared/contexts/EmbedAuthContext';
import { useGetRequest } from '@/shared/hooks/requests';
import { apiErrorMessage } from '@/shared/utils/apiError';
import { postToParent } from '@/shared/utils/embedParentOrigin';
import { fileKind } from '@/shared/utils/fileKind.js';
import { FILES, WORKFLOW } from '@/shared/utils/urls';
import { CollaboraEditor } from '@/modules/collabora-editor/index.jsx';
import { SigningPanel } from '@/modules/signing/components/SigningPanel';
import { EImzoSetupModal } from '@/pages/Auth/EImzoRegistration';

function PdfPreview({ url, title }) {
    // As the viewer does: a blob URL, so the storage host's frame headers
    // don't matter.
    const [blobUrl, setBlobUrl] = useState(null);
    useEffect(() => {
        let made = null;
        let cancelled = false;
        fetch(url)
            .then((r) => r.blob())
            .then((blob) => {
                if (cancelled) return;
                made = URL.createObjectURL(blob);
                setBlobUrl(made);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
            if (made) URL.revokeObjectURL(made);
        };
    }, [url]);
    if (!blobUrl) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
        );
    }
    return <embed src={blobUrl} type="application/pdf" className="w-full h-full dark:invert-[0.9] dark:hue-rotate-180" title={title} />;
}

export default function EmbedSign() {
    const { t } = useTranslation(['signing', 'common']);
    useEmbedAuth();
    const [params] = useSearchParams();
    const fileId = params.get('fileId');
    const valid = Boolean(fileId) && /^\d+$/.test(fileId);

    const workflow = useGetRequest({ showError: false });
    const file = useGetRequest({ showError: false });
    // loading | ready | needsKey | failed
    const [state, setState] = useState('loading');
    const [failure, setFailure] = useState(null);
    const [registering, setRegistering] = useState(false);
    const [tried, setTried] = useState(false);

    const load = useCallback(async () => {
        setState('loading');
        const res = await workflow.request({ url: WORKFLOW.replace('{file_id}', fileId) });
        if (!res.success) {
            if (res.error?.status === 404) {
                setState('needsKey');
            } else {
                setFailure(apiErrorMessage(res.errorInfo));
                setState('failed');
            }
            return;
        }
        // The document itself; a signer who may no longer read it (after
        // signing, under the project's policy) still gets the panel.
        await file.request({ url: `${FILES}/${fileId}` });
        setState('ready');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileId]);

    useEffect(() => {
        if (valid) load();
    }, [valid, load]);

    if (!valid) {
        return <div className="flex items-center justify-center h-dvh text-gray-400">{t('common:embed.noFileId')}</div>;
    }

    if (state === 'loading') {
        return (
            <div className="flex items-center justify-center h-dvh">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (state === 'failed') {
        return (
            <div className="flex flex-col items-center justify-center gap-3 h-dvh p-4 text-center">
                <p className="text-sm text-muted-foreground max-w-md">{failure}</p>
                <Button variant="outline" size="sm" onClick={load}>{t('common:embed.retry')}</Button>
            </div>
        );
    }

    if (state === 'needsKey') {
        return (
            <div className="flex items-center justify-center h-dvh p-4 bg-gray-50 dark:bg-[#0f1112]">
                <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 space-y-4 text-center">
                    <span className="mx-auto w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center">
                        <KeyRound className="w-5 h-5 text-brand" />
                    </span>
                    <h2 className="text-base font-semibold text-foreground">{t('signing:embedSign.keyTitle')}</h2>
                    <p className="text-sm text-muted-foreground">{t('signing:embedSign.keyText')}</p>
                    {tried && <p className="text-sm text-amber-600 dark:text-amber-400">{t('signing:embedSign.stillNotFound')}</p>}
                    <Button onClick={() => setRegistering(true)}>{t('signing:embedSign.confirmKey')}</Button>
                </div>
                <EImzoSetupModal
                    open={registering}
                    onOpenChange={(open) => {
                        setRegistering(open);
                        if (!open) {
                            setTried(true);
                            load();
                        }
                    }}
                />
            </div>
        );
    }

    const doc = file.response || null;
    const kind = fileKind(doc);
    const preview = doc?.previewUrl || doc?.preview_url;
    const hasPreview = Boolean(doc) && (kind === 'office' || (kind === 'pdf' && Boolean(preview)));
    const tell = (change) => postToParent({ type: 'DOCS_SIGNING_CHANGED', fileId: Number(fileId), change });

    // Column on phones (document over the panel), side by side from sm up —
    // the viewer's layout.
    return (
        <div className="h-dvh flex flex-col sm:flex-row bg-gray-50 dark:bg-[#0f1112] overflow-hidden">
            {hasPreview && (
                <div className="flex-1 min-h-0 overflow-hidden">
                    {kind === 'office' ? (
                        <CollaboraEditor fileId={doc.id} readonly={true} />
                    ) : (
                        <PdfPreview url={preview} title={doc.name} />
                    )}
                </div>
            )}
            <div
                className={
                    hasPreview
                        ? 'w-full sm:w-[380px] shrink-0 max-h-[55%] sm:max-h-none border-t sm:border-t-0 sm:border-l border-gray-100 dark:border-white/5 bg-white dark:bg-[#1a1c1e] overflow-y-auto'
                        : 'w-full flex-1 overflow-y-auto flex justify-center'
                }
            >
                <div className="p-4 sm:p-6 dark:text-white">
                    <SigningPanel
                        fileId={Number(fileId)}
                        approvalState="signed"
                        document={doc || undefined}
                        onSigningComplete={() => tell('signed')}
                        onChanged={() => tell('changed')}
                    />
                </div>
            </div>
        </div>
    );
}
